import { skyFoundationSettings } from '../services/configs/configs-service';

/**
 * The default client ID used for authentication.
 * This is used when building the login URL and is appended as a query parameter.
 * The value is set to 'editorialWeb'.
 *
 * @type {string}
 */
export const DEFAULT_CLIENT_ID = 'editorialWeb';

/**
 * Build Infinte scrolling article urls
 * @param {*} url url to build
 */
export const buildInfiniteScrollingArticlesUrl = (url) => {
  const patt = new RegExp('^(http|https)://');
  return patt.test(url) ? url : window.location.origin + url;
};

/**
 * A function that builds Urls
 * @param {string} base the base of the url
 * @param {object} queryParamsObject keys and values that will be encoded to the base url
 * @returns {string} the url
 */
export function buildUrlWithQueryParams (base, queryParamsObject) {
  return Object.keys(queryParamsObject).reduce((acc, curr, index) => {
    return `${acc}${index !== 0 ? '&' : ''}${curr}=${encodeURIComponent(queryParamsObject[curr])}`;
  }, `${base}?`);
}

/**
 * A function that will build the url to get video datas based on the BE configurations using the funcion buildUrlWithQueryParams
 * @param {Object} tabData an object containing the keys and values need to make calls to Videoportal BE
 * @param {String} baseUrl the base of the videoportal API's
 * NOTE: @param {String} baseUrl takes window.SkyFoundationSettings.videoPlaylistBaseUrl which ends with '/'
 */
export const getVideoSearchUrl = (tabData, baseUrl, hlPrevTags = false) => {
  /**
   * hlPrevTags is true
   */
  if (hlPrevTags && tabData.hlPrevTags !== null && tabData.hlPrevTags !== undefined) {
    const queryTags = tabData.hlPrevTags.toString();
    const queryStringObj = {
      tags: queryTags,
      tagsop: 'and',
      count: 50,
      novp: 'no',
      ordby: 'create_date',
      sort: 'd',
      access_type: 0,
      hls: 'yes',
    };
    return buildUrlWithQueryParams(`${baseUrl}getVideoDataSearch`, queryStringObj);
  }
  if (hlPrevTags) {
    return '';
  }

  /**
   * hlPrevTags is false
   */
  if (tabData.playlistType) {
    const queryStringObj = {
      id: tabData.playlistID,
      count: 50,
      novp: 'no',
      free: 0,
      access_type: 0,
      hls: 'yes',
    };
    return buildUrlWithQueryParams(`${baseUrl}getPlaylistVideoData`, queryStringObj);
  }
  if (tabData.tagsType) {
    const queryTags = tabData.tags.toString();

    const queryStringObj = {
      tags: queryTags,
      tagsop: 'and',
      count: 50,
      novp: 'no',
      ordby: 'create_date',
      sort: 'd',
      access_type: 0,
      hls: 'yes',
    };

    return buildUrlWithQueryParams(`${baseUrl}getVideoDataSearch`, queryStringObj);
  }
  if (tabData.sectionsType) {
    const queryStringObj = {
      section: tabData.sectionID,
      tagsop: 'or',
      count: 50,
      novp: 'no',
      ordby: 'create_date',
      sort: 'd',
      access_type: 0,
      hls: 'yes',
    };

    if (tabData.subSectionID && tabData.subSectionID !== '') {
      queryStringObj.subsection = tabData.subSectionID;
    }
    if (tabData.subSubSectionID && tabData.subSubSectionID !== '') {
      queryStringObj.sub_subsection = tabData.subSubSectionID;
    }

    return buildUrlWithQueryParams(`${baseUrl}getVideoDataSearch`, queryStringObj);
  }

  return '';
};

export const getPersonalizeUrl = () => skyFoundationSettings.getPersonalizeUrl();

export const getLogoutUrl = () => skyFoundationSettings.getLogoutBaseUrl();

/**
 * This function is used to get the current location of the window.
 * It returns an object with two properties:
 * - withHostName: This property contains the protocol and hostname of the current window location.
 * - withPathName: This property contains the protocol, hostname, and pathname of the current window location.
 *
 * @returns {Object} An object containing the current window location information.
 * @returns {string} .withHostName The protocol and hostname of the current window location.
 * @returns {string} .withPathName The protocol, hostname, and pathname of the current window location.
 */
export const getLocation = () => ({
  withHostName: `${window.location.protocol}//${window.location.hostname}`,
  withPathName: `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`,
});

/**
 * build the login url
 * @param {String} forwardUrl the url redirect after login
 * @param {String} intcmp optional
 */
export const getLoginUrl = (forwardUrl, intcmp) => {
  let formattedForwardUrl = forwardUrl;
  let redirectUri = getLocation().withHostName;
  if (!redirectUri.endsWith('/')) {
    redirectUri += '/';
  }
  if (!formattedForwardUrl.endsWith('/')) {
    formattedForwardUrl += '/';
  }
  let forwardQueryParam = formattedForwardUrl
    ? `?forward=${formattedForwardUrl}&client_id=${DEFAULT_CLIENT_ID}&redirect_uri=${redirectUri}`
    : '';
  if (forwardQueryParam && intcmp) {
    forwardQueryParam += `&intcmp=${intcmp}`;
  }
  return `${skyFoundationSettings.getLoginBaseUrl()}${forwardQueryParam}`;
};

/**
 * Build url to call the Token Manager on the "/auths" route
 * @param {string} - session id is given for PUT (refresh token) and DELETE
 */
export const getTokenManagerAuthsUrl = (sessionId) => {
  const tokenManagerBaseUrl = skyFoundationSettings.getTokenManagerBaseUrl();
  return sessionId ? `${tokenManagerBaseUrl}/auths/${sessionId}` : `${tokenManagerBaseUrl}/auths`;
};

/**
 * Get hash from the url
 * Use case: https://svilcms.sport.sky.it/tennis/2021/02/16/gallery-con-4-immagini-da-calcio-a-tennis#02 --> "02"
 * @returns {String}
 */
export const getUrlHash = () => window.location.hash.substr(1) || '';

export const getParamFromUrl = (paramName) => {
  const urlParams = new URLSearchParams(window.location.search);
  const paramValue = urlParams.get(paramName);
  return paramValue;
};

export const getUrlWithParam = (name, value, baseUrl = window.location.href) => {
  const param = `${name}=${value}`;
  if (baseUrl.indexOf('?') > -1) {
    return `${baseUrl}&${param}`;
  }
  return `${baseUrl}?${param}`;
};
